'use client';
import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import { XR, XROrigin, createXRStore } from '@react-three/xr';
import * as THREE from 'three';
import { createContext, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { createCameraController } from '../cameraController.js';
import { resolveCameraFar } from '../_pure/spaceCanvasHelpers.js';
import { createSceneSlot } from '../sceneSlot.js';
import SpaceCanvasControls from './SpaceCanvasControls.js';
import SpaceCanvasXrLocomotion from './SpaceCanvasXrLocomotion.js';
import XrSceneFade from './XrSceneFade.js';
import SpaceCanvasXrLeftController from './SpaceCanvasXrLeftController.js';
import SpaceCanvasXrRightController from './SpaceCanvasXrRightController.js';
import { SpaceCanvasXrBindingDriver, SpaceCanvasXrKeyboardDriver, spaceCanvasXrBindings, useXrButtonBinding } from './XrBindings.js';
import XrPassthroughView from './XrPassthroughView.js';
import { useVisualizationDiagnostics } from '../../runtime/diagnostics.js';
import { XrEntryButton } from '../../runtime/components/XrEpisodePlayer.js';
export const spaceCanvasXrStore = createXRStore({
    offerSession: false,
    enterGrantedSession: false,
    // Unused features: tracked hands are rendered over the keyboard, and room scanning costs frame time.
    handTracking: false,
    planeDetection: false,
    meshDetection: false,
    hitTest: false,
    anchors: false,
    controller: { left: SpaceCanvasXrLeftController, right: SpaceCanvasXrRightController },
});
export function CameraControllerBridge(props) {
    const readyRef = useRef(false);
    const { controller, cameraPose, onReady, aspectRef } = props;
    const width = useThree((state) => state.size.width);
    const height = useThree((state) => state.size.height);
    useFrame(({ gl }) => {
        if (width <= 0 || height <= 0)
            return;
        const eye = gl.xr.isPresenting ? gl.xr.getCamera().cameras[0] : undefined;
        aspectRef.current = eye ? eye.projectionMatrix.elements[5] / eye.projectionMatrix.elements[0] : width / height;
        if (controller.read() === null)
            return;
        controller.syncReferenceFrame();
        controller.configure(cameraPose);
        if (!readyRef.current) {
            readyRef.current = true;
            onReady(controller);
        }
        props.onRenderFrame?.();
    }, -60);
    useEffect(() => {
        readyRef.current = false;
        return () => onReady(undefined);
    }, [controller, onReady]);
    return null;
}
const SpaceCanvasHostContext = createContext(null);
const readXrPresenting = () => Boolean(spaceCanvasXrStore.getState().session);
const readNotPresenting = () => false;
function SceneSlotOutlet({ slot }) {
    return _jsx(_Fragment, { children: useSyncExternalStore(slot.subscribe, slot.get, slot.get) });
}
export function WebGlContextStatus({ onChange }) {
    const gl = useThree(state => state.gl);
    useEffect(() => {
        const canvas = gl.domElement;
        const lost = () => {
            console.error('[XR] WebGL context lost. XR entry is disabled until the context is restored.');
            onChange('lost');
        };
        const restored = () => onChange('available');
        canvas.addEventListener('webglcontextlost', lost);
        canvas.addEventListener('webglcontextrestored', restored);
        onChange(gl.getContext().isContextLost() ? 'lost' : 'available');
        return () => {
            canvas.removeEventListener('webglcontextlost', lost);
            canvas.removeEventListener('webglcontextrestored', restored);
        };
    }, [gl, onChange]);
    return null;
}
function CameraInspection({ orbitControlsRef }) {
    const camera = useThree((state) => state.camera);
    useEffect(() => {
        const globals = window;
        const entries = {
            __toolproofCamera: camera,
            __toolproofOrbit: orbitControlsRef,
            __toolproofXr: spaceCanvasXrStore,
            __toolproofDir: () => {
                const target = orbitControlsRef.current?.target;
                const dx = camera.position.x - (target?.x ?? 0);
                const dy = camera.position.y - (target?.y ?? 0);
                const dz = camera.position.z - (target?.z ?? 0);
                const length = Math.sqrt(dx * dx + dy * dy + dz * dz);
                const direction = [dx, dy, dz].map(value => length === 0 ? 0 : Math.round(value / length * 100) / 100);
                console.log('overviewDirection:', JSON.stringify(direction));
                return direction;
            },
        };
        Object.assign(globals, entries);
        return () => {
            for (const [key, value] of Object.entries(entries)) {
                if (globals[key] === value)
                    delete globals[key];
            }
        };
    }, [camera, orbitControlsRef]);
    return null;
}
const hideLegend = () => spaceCanvasXrBindings.setLegendVisible(false);
/** Shortcuts owned by the canvas itself: they work in every space, with or without an episode or agent. */
function SpaceCanvasXrHostBindings({ controller }) {
    useXrButtonBinding('right-stick', { owner: 'XR camera', legend: 'recenter on the view', onTap: () => {
            if (!controller.recenterXr())
                spaceCanvasXrBindings.reject('right', 'No view to recenter on');
        } });
    useXrButtonBinding('left-stick', { owner: 'XR shortcut legend', legend: 'show shortcuts while held',
        onPress: () => spaceCanvasXrBindings.setLegendVisible(true), onRelease: hideLegend, onCancel: hideLegend });
    return null;
}
/**
 * Injects Meta's emulated Quest 3 (IWER) on any origin; @pmndrs/xr injects it automatically only on localhost.
 * It sends the library's own Alt+Meta+E shortcut, which leaves a real XR device untouched.
 */
export function startSpaceCanvasXrEmulator() {
    if (typeof window === 'undefined')
        return;
    window.dispatchEvent(new KeyboardEvent('keydown', { altKey: true, metaKey: true, code: 'KeyE', key: 'e' }));
}
/**
 * Owns the canvas, XR session, camera rig, controls and camera controller so they can outlive the
 * space rendered into them. Spaces render their scene through SpaceCanvas, which fills this host's slot.
 */
export function SpaceCanvasHost(props) {
    const { cameraInspection } = useVisualizationDiagnostics();
    useEffect(() => {
        if (props.xrEmulator)
            startSpaceCanvasXrEmulator();
    }, [props.xrEmulator]);
    const cameraRef = useRef(null);
    const rendererRef = useRef(null);
    const orbitControlsRef = useRef(null);
    const cameraRigRef = useRef(null);
    const aspectRef = useRef(1);
    const xrCameraRef = useRef(null);
    const xrEntryPause = useRef(undefined);
    const mixedReality = useRef(false);
    useEffect(() => {
        // Detected ahead of entry: awaiting it on click could spend the user activation the session request needs.
        let mounted = true;
        if (typeof navigator === 'undefined')
            return;
        void navigator.xr?.isSessionSupported?.('immersive-ar').then(value => { if (mounted)
            mixedReality.current = value; }, () => undefined);
        return () => { mounted = false; };
    }, []);
    const [contextStatus, setContextStatus] = useState('loading');
    const [navigationMode, setNavigationMode] = useState('free');
    const [slot] = useState(createSceneSlot);
    const controller = useMemo(() => createCameraController({
        camera: () => cameraRef.current,
        controls: () => orbitControlsRef.current,
        rig: () => cameraRigRef.current,
        isXr: () => Boolean(spaceCanvasXrStore.getState().session),
        onModeChange: setNavigationMode,
        viewportAspect: () => aspectRef.current,
        xrCamera: () => xrCameraRef.current,
    }), []);
    const handle = useMemo(() => ({
        controller, cameraRigRef, aspectRef, slot, xrEntryPause,
        canNavigate: () => controller.getMode() === 'free',
    }), [controller, slot]);
    const active = useSyncExternalStore(slot.subscribe, slot.active, slot.active);
    // An XR session keeps rendering between scenes; the headset shows XrSceneFade instead of nothing.
    const presenting = useSyncExternalStore(spaceCanvasXrStore.subscribe, readXrPresenting, readNotPresenting);
    const [initialCamera] = useState(() => props.initialCameraPose ? {
        position: props.initialCameraPose.position,
        fov: props.initialCameraPose.fov,
        far: resolveCameraFar(props.initialCameraPose),
    } : undefined);
    return (_jsxs(SpaceCanvasHostContext.Provider, { value: handle, children: [props.children, _jsx(XrEntryButton, { onPause: () => xrEntryPause.current?.(), ready: active && contextStatus === 'available', onEnter: async () => {
                    const renderer = rendererRef.current;
                    if (!renderer || renderer.getContext().isContextLost()) {
                        setContextStatus('lost');
                        throw new Error('Graphics context is unavailable. Reload the page before entering XR.');
                    }
                    try {
                        // Mixed reality looks the same as VR until passthrough is chosen in-headset (P / K).
                        return await (mixedReality.current ? spaceCanvasXrStore.enterAR() : spaceCanvasXrStore.enterVR());
                    }
                    catch (failure) {
                        const session = renderer.xr.getSession();
                        if (session) {
                            try {
                                await session.end();
                            }
                            catch (cleanupFailure) {
                                console.error('[XR] Failed session cleanup:', cleanupFailure);
                            }
                        }
                        throw failure;
                    }
                } }), contextStatus === 'lost' ? _jsxs("div", { role: "alert", style: {
                    position: 'absolute', top: 70, right: 16, zIndex: 20, background: '#7f1d1d', color: 'white', padding: 12,
                }, children: ["Graphics context lost. Exit XR and reload this page if it does not recover.", _jsx("button", { type: "button", onClick: () => window.location.reload(), children: "Reload page" })] }) : null, _jsxs(Canvas, { style: { width: '100%', height: '100%', ...(active || presenting ? {} : { display: 'none' }) }, 
                // Live portal previews clip their scene to the portal opening with the stencil buffer.
                gl: { stencil: true }, ...(initialCamera ? { camera: initialCamera } : {}), onCreated: ({ camera, gl }) => {
                    cameraRef.current = camera;
                    rendererRef.current = gl;
                    xrCameraRef.current = gl.xr.getCamera();
                }, children: [_jsx(WebGlContextStatus, { onChange: setContextStatus }), cameraInspection ? _jsx(CameraInspection, { orbitControlsRef: orbitControlsRef }) : null, _jsxs(XR, { store: spaceCanvasXrStore, children: [_jsx(SpaceCanvasXrBindingDriver, {}), _jsx(SpaceCanvasXrKeyboardDriver, {}), _jsx(SpaceCanvasXrHostBindings, { controller: controller }), _jsxs(XROrigin, { ref: cameraRigRef, children: [_jsx(XrPassthroughView, {}), _jsx(XrSceneFade, { slot: slot })] }), _jsx(SpaceCanvasControls, { navigationMode: navigationMode, orbitControlsRef: orbitControlsRef }), _jsx(SceneSlotOutlet, { slot: slot }), props.sceneOverlay] })] })] }));
}
function SpaceCanvasScene(props) {
    const { host, cameraPose } = props;
    const travelSceneRef = useRef(null);
    const scene = useThree((state) => state.scene);
    // A space's background is fixed when its scene mounts.
    const [background] = useState(props.background ?? 'skyblue');
    useLayoutEffect(() => {
        scene.background = new THREE.Color(background);
    }, [scene, background]);
    return (_jsxs(_Fragment, { children: [_jsx(CameraControllerBridge, { controller: host.controller, cameraPose: cameraPose, onReady: props.onCameraControllerReady, aspectRef: host.aspectRef, ...(props.onRenderFrame ? { onRenderFrame: props.onRenderFrame } : {}) }), _jsx(SpaceCanvasXrLocomotion, { canNavigate: host.canNavigate, cameraRigRef: host.cameraRigRef, travelSceneRef: travelSceneRef }), _jsx("group", { ref: travelSceneRef, children: props.children })] }));
}
let nextSceneKey = 0;
function SpaceCanvasSlot(props) {
    const host = useContext(SpaceCanvasHostContext);
    if (!host)
        throw new Error('SpaceCanvasSlot requires a SpaceCanvasHost.');
    const [owner] = useState(() => ({ key: `scene-${nextSceneKey++}` }));
    const content = _jsx(SpaceCanvasScene, { ...props, host: host }, owner.key);
    useLayoutEffect(() => {
        // Each scene starts with the camera state a freshly created canvas would have.
        host.controller.reset();
        return () => host.slot.clear(owner);
    }, [host, owner]);
    useLayoutEffect(() => {
        host.slot.set(owner, content);
    });
    useLayoutEffect(() => {
        const pause = props.onXrEntryPause;
        host.xrEntryPause.current = pause;
        return () => {
            if (host.xrEntryPause.current === pause)
                host.xrEntryPause.current = undefined;
        };
    }, [host, props.onXrEntryPause]);
    return null;
}
/** Renders a space's scene into the surrounding SpaceCanvasHost, or into its own host when there is none. */
export default function SpaceCanvas(props) {
    const host = useContext(SpaceCanvasHostContext);
    if (host)
        return _jsx(SpaceCanvasSlot, { ...props });
    return (_jsx(SpaceCanvasHost, { initialCameraPose: props.cameraPose, children: _jsx(SpaceCanvasSlot, { ...props }) }));
}
